import { EMPTY_REFINEMENTS, type BoxTruckRefinements } from '../components/BoxTruckRefinementBar'
import { isRecord } from '@/utils/isRecord'

/**
 * The single validator for the board's CLIENT-SIDE filter values as they cross a storage boundary.
 *
 * Two paths carry these: the browser-tab handoff (utils/browserTabHandoff.ts) and the per-window view-prefs
 * record (hooks/useBoxTruckViewPrefs.ts). Both read something a user could hand-edit, and both must answer
 * the same way — two copies of a bound is two bounds that can drift, and the one that drifts looser is the
 * one nobody notices.
 */

const SOURCE_KEY_PATTERN = /^[A-Z0-9_-]{1,64}$/
const MAX_HIDDEN_SOURCES = 50

/** Match the refinement inputs' own keystroke grammar, so a stored value can never narrow a board in a way
 *  the controls themselves cannot express — and so cannot be cleared through them either. */
const refinementValue = (value: unknown): string => (typeof value === 'string' && value.length <= 32 && /^\d*\.?\d*$/.test(value) ? value : '')

/**
 * An unrecognised age or contact degrades to '' — which is "All", the WIDEST setting. A stale or corrupt
 * record therefore shows MORE loads, never fewer: the board can never hide rows for a reason the operator
 * cannot see in the controls.
 */
export const sanitizeRefinements = (value: unknown): BoxTruckRefinements => {
    const record = isRecord(value) ? value : {}
    return {
        ...EMPTY_REFINEMENTS,
        minMi: refinementValue(record.minMi),
        maxMi: refinementValue(record.maxMi),
        minR: refinementValue(record.minR),
        minT: refinementValue(record.minT),
        minWeight: refinementValue(record.minWeight),
        maxWeight: refinementValue(record.maxWeight),
        contact: record.contact === 'email' || record.contact === 'phone' ? record.contact : '',
    }
}

/** Deselected Sources-bar chips: known-shaped keys only, deduped and capped. */
export const sanitizeHiddenSources = (value: unknown): string[] =>
    Array.isArray(value)
        ? [...new Set(value.filter((source): source is string => typeof source === 'string' && SOURCE_KEY_PATTERN.test(source)))].slice(0, MAX_HIDDEN_SOURCES)
        : []
